
/** Operators that negate each other when AND'd on the same field + value. */
const OPPOSING_OPERATORS = {
  equals: 'not_equals',
  not_equals: 'equals',
  contains: 'does_not_contain',
  does_not_contain: 'contains',
  in: 'not_in',
  not_in: 'in',
  is_empty: 'is_not_empty',
  is_not_empty: 'is_empty',
  is_null: 'is_not_null',
  is_not_null: 'is_null',
};

const VALUELESS_OPERATORS = new Set([
  'is_empty',
  'is_not_empty',
  'is_null',
  'is_not_null',
]);

class ValidationService {
  validateStep(stepIndex, state, metadata) {
    const validators = {
      0: this.validateTriggerStep,
      1: this.validateConditionsStep,
      2: this.validateActionsStep,
      3: this.validateReviewStep
    };

    const validator = validators[stepIndex];
    if (!validator) return { valid: true, errors: {} };

    return validator.call(this, state, metadata);
  }

  validateTriggerStep(state, metadata) {
    const errors = {};

    if (!state.wizardData.name?.trim()) {
      errors.name = 'Automation name is required';
    }

    if (!state.wizardData.object) {
      errors.object = 'Please select an object';
    }

    if (!state.wizardData.trigger) {
      errors.trigger = 'Please select a trigger event';
    } else if (state.wizardData.object && state.wizardData.trigger) {
      // Use metadata to validate trigger compatibility
      const objectSchema = metadata.objectMetadata[state.wizardData.object];
      // "on schedule" is the flow builder's; it applies to any object.
      if (state.wizardData.trigger !== 'on schedule' && objectSchema && !objectSchema.capabilities?.triggers?.includes(state.wizardData.trigger)) {
        errors.trigger = `${state.wizardData.trigger} is not supported for ${state.wizardData.object}`;
      }
    }

    return {
      valid: Object.keys(errors).length === 0,
      errors
    };
  }

  normalizeConditionValue(value) {
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    }
    return String(value).trim().toLowerCase();
  }

  /**
   * Flag condition pairs that can never be true together under AND.
   * Example: Lead Source equals Website AND Lead Source not equals Website.
   */
  findContradictoryConditions(conditions, errorPrefix) {
    const errors = {};
    if (!conditions || conditions.length < 2) return errors;

    for (let i = 0; i < conditions.length; i++) {
      const a = conditions[i];
      if (!a?.field || !a?.operator) continue;

      for (let j = i + 1; j < conditions.length; j++) {
        const b = conditions[j];
        if (!b?.field || !b?.operator) continue;
        if (a.field !== b.field) continue;

        const opposing = OPPOSING_OPERATORS[a.operator];
        const valuesMatch =
          VALUELESS_OPERATORS.has(a.operator) ||
          VALUELESS_OPERATORS.has(b.operator) ||
          this.normalizeConditionValue(a.value) === this.normalizeConditionValue(b.value);

        if (opposing && b.operator === opposing && valuesMatch) {
          const key = `${errorPrefix}_condition_${j}_operator`;
          errors[key] =
            `Contradictory conditions on the same field: "${a.operator}" and "${b.operator}" can never both be true. Remove or change one.`;
          continue;
        }

        // Two different equals values on the same field under AND can never match
        if (
          a.operator === 'equals' &&
          b.operator === 'equals' &&
          this.normalizeConditionValue(a.value) !== this.normalizeConditionValue(b.value) &&
          a.value !== '' &&
          a.value !== undefined &&
          b.value !== '' &&
          b.value !== undefined
        ) {
          const key = `${errorPrefix}_condition_${j}_value`;
          errors[key] =
            `Contradictory conditions: field cannot equal two different values at once. Remove or change one.`;
        }
      }
    }

    return errors;
  }

  validateConditionsStep(state, metadata) {
    const errors = {};
    const andGroupConditions = [];

    state.conditionGroups.forEach((group, groupIndex) => {
      if (group.conditions && group.conditions.length > 0) {
        // Validate advanced logic if enabled
        if (group.logicMode === 'advanced' && group.conditions.length > 1) {
          const validation = this.validateCustomLogic(group.customLogic, group.conditions.length);
          if (!validation.valid) {
            errors[`group_${groupIndex}_logic`] = validation.error;
          }
        }

        // Validate each condition
        group.conditions.forEach((condition, condIndex) => {
          if (!condition.field) {
            errors[`group_${groupIndex}_condition_${condIndex}_field`] = 'Field is required';
          }
          
          if (!condition.operator) {
            errors[`group_${groupIndex}_condition_${condIndex}_operator`] = 'Operator is required';
          }
          
          // Check if value is required (is_empty / is_not_empty need none)
          if (
            condition.operator &&
            !VALUELESS_OPERATORS.has(condition.operator) &&
            (condition.value === null ||
              condition.value === undefined ||
              condition.value === '')
          ) {
            errors[`group_${groupIndex}_condition_${condIndex}_value`] = 'Value is required';
          }
        });

        // Within-group contradictions only apply when the group is AND'd
        // (simple OR groups can still match; advanced custom logic is ambiguous)
        const groupIsAnd =
          (group.logic || 'AND').toUpperCase() === 'AND' &&
          group.logicMode !== 'advanced';

        if (groupIsAnd) {
          Object.assign(
            errors,
            this.findContradictoryConditions(
              group.conditions,
              `group_${groupIndex}`
            )
          );
          andGroupConditions.push(
            ...group.conditions.map((c, condIndex) => ({
              ...c,
              _errorPrefix: `group_${groupIndex}`,
              _condIndex: condIndex,
            }))
          );
        }
      }
    });

    // Across groups when groupLogic is AND, opposing conditions also never match
    if ((state.groupLogic || 'AND').toUpperCase() === 'AND' && andGroupConditions.length >= 2) {
      const crossErrors = this.findContradictoryConditions(
        andGroupConditions,
        'cross_group'
      );
      // Remap keys to the second condition's real group/index when possible
      Object.entries(crossErrors).forEach(([key, message]) => {
        const match = key.match(/^cross_group_condition_(\d+)_(.+)$/);
        if (match) {
          const idx = parseInt(match[1], 10);
          const suffix = match[2];
          const target = andGroupConditions[idx];
          if (target) {
            errors[`${target._errorPrefix}_condition_${target._condIndex}_${suffix}`] = message;
            return;
          }
        }
        errors[key] = message;
      });
    }

    return {
      valid: Object.keys(errors).length === 0,
      errors
    };
  }

  validateActionsStep(state, metadata) {
    const errors = {};

    if (state.actions.length === 0) {
      errors.actions = 'At least one action is required';
    }

    state.actions.forEach((action, index) => {
      if (!action.type) {
        errors[`action_${index}_type`] = 'Action type is required';
      }

      if (action.type === 'Send Email') {
        if (!action.parameters?.toEmail) {
          errors[`action_${index}_toEmail`] = 'Recipient is required';
        }
        if (!action.parameters?.content && !action.parameters?.template) {
          errors[`action_${index}_content`] = 'Email content or a template is required';
        }
      }

      if (action.type === 'Create Task') {
        if (!String(action.parameters?.subject || '').trim()) {
          errors[`action_${index}_subject`] = 'Task title is required';
        }
        // Relative amounts are clamped to >= 0 in DateField. Absolute past
        // dates are also enforced server-side on save; surface them here so
        // the error renders next to the Due Date field. Unchanged baseline
        // values (edit of a legacy automation) are allowed through.
        const due = action.parameters?.due_date;
        const baseline = state.baselineAbsoluteDueDates || [];
        if (due && typeof due !== 'object' && !baseline.includes(String(due))) {
          const parsed = Date.parse(String(due));
          if (!Number.isNaN(parsed) && parsed < Date.now()) {
            errors[`action_${index}_due_date`] =
              'Due date cannot be in the past';
          }
        }
      }

      // Get action schema from metadata
      const actionSchema = metadata.actionSchemas?.[action.type];
      if (actionSchema) {
        // Validate required parameters
        Object.entries(actionSchema.required_fields || {}).forEach(([field, label]) => {
          if (!action.parameters?.[field]) {
            errors[`action_${index}_${field}`] = `${label} is required`;
          }
        });

        // Custom validation function from schema
        if (actionSchema.validate) {
          const customErrors = actionSchema.validate(action, metadata);
          Object.assign(errors, customErrors);
        }
      }

      // Validate time delay if enabled
      if (action.timeDelay?.enabled) {
        const totalDelay = (action.timeDelay.days || 0) + 
                          (action.timeDelay.hours || 0) + 
                          (action.timeDelay.minutes || 0);
        if (totalDelay === 0) {
          errors[`action_${index}_timeDelay`] = 'Please specify a delay time';
        }
      }
    });

    return {
      valid: Object.keys(errors).length === 0,
      errors
    };
  }

  validateReviewStep(state, metadata) {
    // Run all validations
    const triggerValidation = this.validateTriggerStep(state, metadata);
    const conditionsValidation = this.validateConditionsStep(state, metadata);
    const actionsValidation = this.validateActionsStep(state, metadata);

    const allErrors = {
      ...triggerValidation.errors,
      ...conditionsValidation.errors,
      ...actionsValidation.errors
    };

    return {
      valid: Object.keys(allErrors).length === 0,
      errors: allErrors
    };
  }

  validateCustomLogic(logic, conditionCount) {
    if (!logic?.trim()) return { valid: true };

    const numbers = logic.match(/\d+/g) || [];
    const uniqueNumbers = [...new Set(numbers.map(n => parseInt(n)))];

    for (let num of uniqueNumbers) {
      if (num < 1 || num > conditionCount) {
        return { 
          valid: false, 
          error: `Invalid condition number: ${num}. Must be between 1 and ${conditionCount}` 
        };
      }
    }

    // Check for balanced parentheses
    const openParens = (logic.match(/\(/g) || []).length;
    const closeParens = (logic.match(/\)/g) || []).length;
    
    if (openParens !== closeParens) {
      return { valid: false, error: 'Unbalanced parentheses' };
    }

    // Check for valid operators
    const validTokens = /^[\d\s$\)]+$|^[\d\s\($ANDOR]+$/i;
    if (!validTokens.test(logic)) {
      return { valid: false, error: 'Invalid characters. Use only numbers, AND, OR, and parentheses' };
    }

    return { valid: true };
  }
}

export default new ValidationService();
